import React from "react";
import {
  BLEED, C, clamp, lerp, seg, smooth, ease, mixColor, rng, wire, Worker,
} from "../kit";

// Act II · The Homeplace (the first verse).
// The family's old place after the work dried up, told through things left
// behind. Beats (p):
//   0-.24    the front room at dusk: his chair, his ashtray, his glass. The
//            last curl of smoke rises and goes out for good; dust settles.
//   .2-.5    out back, the tomato garden gone to ruin; a rat works the row
//   .45-.77  his pickup in the yard -> the same truck at the drive-in, warm,
//            kids in the bed -> back to now: flat tyres, sinking, grass up
//            round the wheels, night coming on
//   .74-1    the one warm kitchen window at night: six kids at the table and
//            an empty chair at its head
// Every SVG id starts with "hp-".

const frac = (v) => v - Math.floor(v);

// ===========================================================================
// 1 · The front room
// ===========================================================================

const WB = 640; // where the wall meets the floor
const SEAMS = (() => {
  const ys = [];
  let y = WB;
  let g = 9;
  while (y < 1200) {
    ys.push(y);
    y += g;
    g *= 1.17;
  }
  return ys;
})();
const SEAMS_D = SEAMS.map((y) => `M -420 ${y} H 2020`).join(" ");
const JOINTS_D = (() => {
  const r = rng(4040);
  const d = [];
  for (let i = 0; i < SEAMS.length - 1; i++) {
    const y0 = SEAMS[i];
    const y1 = SEAMS[i + 1];
    const len = 260 + (y1 - y0) * 9;
    let x = -420 + r() * len;
    while (x < 2020) {
      d.push(`M ${x} ${y0} V ${y1}`);
      x += len * (0.6 + r() * 0.7);
    }
  }
  return d.join(" ");
})();
// Grain streaks along the boards.
const GRAIN_D = (() => {
  const r = rng(4141);
  const d = [];
  for (let i = 1; i < SEAMS.length - 1; i++) {
    const y0 = SEAMS[i];
    const h = SEAMS[i + 1] - y0;
    for (let k = 0; k < 7; k++) {
      const x = -400 + r() * 2400;
      const y = y0 + h * (0.25 + r() * 0.5);
      const w = 60 + r() * 220;
      d.push(`M ${x} ${y} q ${w / 2} ${(r() - 0.5) * h * 0.3} ${w} 0`);
    }
  }
  return d.join(" ");
})();
// Where his boots wore the finish off: door to chair.
const WORN_D = "M 176 641 C 430 650 700 684 872 744 L 1064 744 C 860 682 520 648 330 641 Z";
const WORN2_D = "M 214 641 C 440 652 720 690 900 744 L 1030 744 C 850 684 520 650 300 641 Z";

const WIN = { x0: 480, x1: 730, y0: 236, y1: 520, mid: 378 };
const DOOR = { x0: 160, x1: 310, top: 300 };
const CHAIR_BACK_D = "M 862 600 L 862 410 Q 862 366 906 361 Q 960 353 1014 361 Q 1058 366 1058 410 L 1058 600 Z";
const CHAIR_D = [
  // wings
  "M 868 400 C 836 404 816 426 814 462 L 814 540 L 868 540 Z",
  "M 1052 400 C 1084 404 1104 426 1106 462 L 1106 540 L 1052 540 Z",
  // rolled arms
  "M 796 550 Q 796 527 820 525 L 846 525 Q 870 527 870 552 L 870 704 L 800 704 Z",
  "M 1050 552 Q 1050 527 1074 525 L 1100 525 Q 1124 527 1124 550 L 1120 704 L 1050 704 Z",
  // seat, sagging where he sat
  "M 866 590 Q 960 606 1054 590 L 1054 642 L 866 642 Z",
  // skirt and feet
  "M 800 640 H 1120 V 700 H 800 Z",
  "M 812 700 h 14 l -3 26 h -8 Z M 1094 700 h 14 l -3 26 h -8 Z",
].join(" ");
const CHAIR_RIM_D =
  "M 862 600 L 862 410 Q 862 366 906 361 M 814 540 L 814 462 C 816 426 836 404 868 400 M 796 550 Q 796 527 820 525 L 846 525 M 866 590 Q 900 596 930 599";
const DOILY_D = (() => {
  let d = "M 912 362 Q 960 355 1008 362 L 1008 380";
  for (let i = 0; i < 8; i++) {
    const x0 = 1008 - i * 12;
    d += ` Q ${x0 - 6} ${392 + (i % 2) * 2} ${x0 - 12} 380`;
  }
  return d + " Z";
})();

const TBL = { x: 1222, top: 552 };
const TABLE_D = [
  `M ${TBL.x - 76} ${TBL.top} V ${TBL.top + 8} A 76 11 0 0 0 ${TBL.x + 76} ${TBL.top + 8} V ${TBL.top} Z`,
  `M 1214 562 L 1212 600 Q 1205 611 1212 622 L 1214 690 L 1230 690 L 1232 622 Q 1239 611 1232 600 L 1230 562 Z`,
  "M 1222 686 Q 1190 699 1168 716 L 1180 719 Q 1200 707 1222 699 Q 1244 707 1264 719 L 1276 716 Q 1254 699 1222 686 Z",
].join(" ");
const ASH = { x: 1186, y: 549 };
const GLASS = { x: 1254, y: 554 };
const GLASS_D = `M ${GLASS.x - 17} ${GLASS.y - 50} L ${GLASS.x - 15} ${GLASS.y - 3} Q ${GLASS.x - 15} ${GLASS.y} ${GLASS.x - 11} ${GLASS.y} L ${GLASS.x + 11} ${GLASS.y} Q ${GLASS.x + 15} ${GLASS.y} ${GLASS.x + 15} ${GLASS.y - 3} L ${GLASS.x + 17} ${GLASS.y - 50} Z`;
const EMBER = { x: 1177, y: 543 };

const MOTES = (() => {
  const r = rng(3131);
  return Array.from({ length: 30 }, () => ({
    u: r(), v: r(), rate: 0.15 + r() * 0.3, ph: r() * 6.28, size: 0.8 + r() * 1.4, tw: 0.6 + r() * 2,
  }));
})();
const OUTSIDE_D =
  "M 470 470 Q 520 462 560 468 L 560 450 Q 575 430 590 452 L 590 466 L 700 470 L 740 470 L 740 530 L 470 530 Z " +
  "M 650 470 L 652 404 L 656 404 L 658 470 Z M 640 408 h 28 v 4 h -28 Z";

function smokeD(x0, y0, local, h0, h1) {
  const pts = [];
  for (let h = h0; h <= h1; h += 5) {
    const amp = 1.2 + h * 0.085;
    const x = x0 + amp * Math.sin(h * 0.045 - local * 1.6) + h * 0.035 * Math.sin(h * 0.1 - local * 1.05 + 2);
    pts.push(`${pts.length ? "L" : "M"} ${x.toFixed(1)} ${(y0 - h).toFixed(1)}`);
  }
  return pts.join(" ");
}

function Parlor({ p, local, dur }) {
  const k = seg(p, 0, 0.245);
  const sun = 1 - 0.35 * k; // the light goes as the sun does
  // The cigarette goes out at p .12; its last curl drifts up and is gone by .18.
  const outAt = 0.118 * dur;
  const since = local - outAt;
  const ember = since < 0 ? 0.75 + 0.25 * Math.sin(local * 2.3) * Math.sin(local * 0.9 + 1) : Math.max(0, 1 - since / 0.8);
  const h0 = since < 0 ? 0 : since * 34;
  const h1 = 150 + Math.max(0, since) * 22;
  const smokeK = since < 0 ? 1 : 1 - smooth(clamp(since / 2.2));
  const dust = smooth(seg(p, 0.08, 0.24));
  const push = 1 + 0.12 * ease.inOut(seg(p, 0, 0.26));
  // At the end the camera tilts down to the empty boards and the garden
  // picks up from its soil, so the cut rides the move.
  const tiltDown = 560 * ease.inOut(seg(p, 0.19, 0.236));
  const cx = 1150;
  const cy = 540;
  const sway = Math.sin(local * 0.7) * 3 + Math.sin(local * 1.9) * 1.2;
  const sky = [
    mixColor("#3a2f4c", "#2a2238", k),
    mixColor("#b7616a", "#7d4a5c", k),
    mixColor("#f3a45e", "#c97a4c", k),
  ];

  return (
    <g transform={`translate(0 ${-tiltDown}) translate(${cx} ${cy}) scale(${push}) translate(${-cx} ${-cy})`}>
      <defs>
        <linearGradient id="hp-paper" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={WB}>
          <stop offset="0" stopColor="#2a1d14" />
          <stop offset="1" stopColor="#3e2b1d" />
        </linearGradient>
        <radialGradient id="hp-wallglow" gradientUnits="userSpaceOnUse" cx={WIN.x0 + 180} cy={WIN.y0 + 200} r={900}>
          <stop offset="0" stopColor={C.warm} stopOpacity="0.42" />
          <stop offset="0.4" stopColor={C.warmDeep} stopOpacity="0.22" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.55" />
        </radialGradient>
        <linearGradient id="hp-dusk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={sky[0]} />
          <stop offset="0.55" stopColor={sky[1]} />
          <stop offset="1" stopColor={sky[2]} />
        </linearGradient>
        <linearGradient id="hp-oak" gradientUnits="userSpaceOnUse" x1="0" y1={WB} x2="0" y2="900">
          <stop offset="0" stopColor="#2c1d12" />
          <stop offset="1" stopColor="#4a321f" />
        </linearGradient>
        <linearGradient id="hp-beam" gradientUnits="userSpaceOnUse" x1={WIN.x0} y1={WIN.y0} x2={1000} y2={760}>
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.2" />
          <stop offset="1" stopColor={C.warmHot} stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="hp-upholstery" gradientUnits="userSpaceOnUse" x1="790" y1="0" x2="1130" y2="0">
          <stop offset="0" stopColor="#5b3826" />
          <stop offset="0.5" stopColor="#3a2318" />
          <stop offset="1" stopColor="#1e120c" />
        </linearGradient>
        <linearGradient id="hp-wood" gradientUnits="userSpaceOnUse" x1="1140" y1="0" x2="1300" y2="0">
          <stop offset="0" stopColor="#6a4429" />
          <stop offset="1" stopColor="#2a180d" />
        </linearGradient>
        <linearGradient id="hp-smoke" gradientUnits="userSpaceOnUse" x1="0" y1={EMBER.y} x2="0" y2={EMBER.y - 190}>
          <stop offset="0" stopColor="#efe6da" stopOpacity="0.75" />
          <stop offset="0.6" stopColor="#efe6da" stopOpacity="0.3" />
          <stop offset="1" stopColor="#efe6da" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hp-tumbler" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f6e4c2" stopOpacity="0.5" />
          <stop offset="0.3" stopColor="#9a7a58" stopOpacity="0.18" />
          <stop offset="1" stopColor="#2a1a10" stopOpacity="0.35" />
        </linearGradient>
        <pattern id="hp-sprig" width="44" height="44" patternUnits="userSpaceOnUse">
          <path d="M 22 8 L 25 14 L 22 20 L 19 14 Z M 0 30 L 3 36 L 0 42 L -3 36 Z M 44 30 L 47 36 L 44 42 L 41 36 Z"
            fill="#e9c08a" fillOpacity="0.07" />
          <path d="M 11 0 V 44" stroke="#000" strokeOpacity="0.08" strokeWidth="1" />
        </pattern>
        <clipPath id="hp-win">
          <rect x={WIN.x0} y={WIN.y0} width={WIN.x1 - WIN.x0} height={WIN.y1 - WIN.y0} />
        </clipPath>
        <clipPath id="hp-glass">
          <path d={GLASS_D} />
        </clipPath>
      </defs>

      {/* Wall */}
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={WB - BLEED.y} fill="url(#hp-paper)" />
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={WB - BLEED.y} fill="url(#hp-sprig)" />
      <rect x={BLEED.x} y={468} width={BLEED.w} height={WB - 468} fill="#1d130c" />
      <path d={`M ${BLEED.x} 468 H ${BLEED.x + BLEED.w} M ${BLEED.x} ${WB - 14} H ${BLEED.x + BLEED.w}`} stroke="#4a3220" strokeWidth={4} />

      {/* Window and the dusk outside */}
      <rect x={WIN.x0} y={WIN.y0} width={WIN.x1 - WIN.x0} height={WIN.y1 - WIN.y0} fill="url(#hp-dusk)" />
      <g clipPath="url(#hp-win)">
        <circle cx={612} cy={lerp(486, 506, k)} r={22} fill="#ffd6a0" opacity={0.85 * sun} />
        <path d={OUTSIDE_D} fill="#1b1016" />
        <g transform={`rotate(${local * 22} 654 404)`} stroke="#1b1016" strokeWidth={2.4}>
          <path d="M 654 404 V 384 M 654 404 L 671 414 M 654 404 L 637 414" />
        </g>
      </g>
      <path
        d={`M ${WIN.x0} ${WIN.y0} H ${WIN.x1} V ${WIN.y1} H ${WIN.x0} Z M ${(WIN.x0 + WIN.x1) / 2} ${WIN.y0} V ${WIN.y1} ` +
          `M ${WIN.x0} ${(WIN.y0 + WIN.mid) / 2} H ${WIN.x1} M ${WIN.x0} ${(WIN.mid + WIN.y1) / 2} H ${WIN.x1}`}
        stroke="#1a0f08" strokeWidth={6} fill="none"
      />
      <path d={`M ${WIN.x0} ${WIN.mid} H ${WIN.x1}`} stroke="#1a0f08" strokeWidth={11} />
      <path d={`M ${WIN.x0 - 14} ${WIN.y0 - 14} H ${WIN.x1 + 14} V ${WIN.y1 + 4} M ${WIN.x0 - 14} ${WIN.y0 - 14} V ${WIN.y1 + 4}`}
        stroke="#3a2416" strokeWidth={12} fill="none" />
      <rect x={WIN.x0 - 26} y={WIN.y1} width={WIN.x1 - WIN.x0 + 52} height={12} fill="#4a2f1c" />
      {/* sheers */}
      <path d={`M ${WIN.x0 - 18} ${WIN.y0 - 10} Q ${WIN.x0 + 26} ${WIN.y0 + 140} ${WIN.x0 + 8} ${WIN.y1 - 10 + sway * 0.3} L ${WIN.x0 - 22} ${WIN.y1 - 8} Z`}
        fill="#f3e3c8" opacity={0.26} />
      <path d={`M ${WIN.x1 + 18} ${WIN.y0 - 10} Q ${WIN.x1 - 26 + sway} ${WIN.y0 + 140} ${WIN.x1 - 8 + sway} ${WIN.y1 - 10} L ${WIN.x1 + 22} ${WIN.y1 - 8} Z`}
        fill="#f3e3c8" opacity={0.26} />

      {/* Doorway and his peg rail */}
      <rect x={DOOR.x0} y={DOOR.top} width={DOOR.x1 - DOOR.x0} height={WB - DOOR.top} fill="#0b0705" />
      <path d={`M ${DOOR.x0 - 7} ${WB} V ${DOOR.top - 7} H ${DOOR.x1 + 7} V ${WB}`} stroke="#3a2416" strokeWidth={14} fill="none" />
      <rect x={344} y={346} width={104} height={10} fill="#3a2416" />
      <circle cx={366} cy={351} r={4} fill="#1a0f08" />
      <circle cx={424} cy={351} r={4} fill="#1a0f08" />
      <path d="M 352 352 Q 352 334 366 333 Q 381 334 381 352 Z M 377 349 L 394 353 L 377 354 Z" fill="#0f0906" />
      <path d="M 416 351 Q 404 380 402 470 Q 412 482 424 476 Q 438 482 448 470 Q 444 380 432 351 Z" fill="#100a07" />

      {/* Floor */}
      <rect x={BLEED.x} y={WB} width={BLEED.w} height={BLEED.y + BLEED.h - WB} fill="url(#hp-oak)" />
      <path d={GRAIN_D} stroke="#1e1209" strokeOpacity={0.35} strokeWidth={1} fill="none" />
      <path d={WORN2_D} fill="#8a6440" opacity={0.12} />
      <path d={WORN_D} fill="#9a7048" opacity={0.14} />
      <path d={SEAMS_D} stroke="#140b06" strokeWidth={1.6} fill="none" />
      <path d={JOINTS_D} stroke="#140b06" strokeWidth={1.4} fill="none" />

      {/* Wall light from the window, and the window's patch on the boards */}
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#hp-wallglow)" opacity={sun} />
      <g opacity={0.8 * sun}>
        <path d="M 656 648 L 898 648 L 1012 744 L 752 744 Z" fill={C.warmHot} opacity={0.2} />
        <path d="M 777 648 L 882 744 M 704 694 L 958 694" stroke="#2a180d" strokeWidth={9} opacity={0.5} />
      </g>
      <path d={`M ${WIN.x0} ${WIN.y0} L ${WIN.x1} ${WIN.y0} L 1012 744 L 752 744 L ${WIN.x0} ${WIN.y1} Z`} fill="url(#hp-beam)" opacity={sun} />

      {/* The chair, its shadow on the wall */}
      <path d={CHAIR_BACK_D} fill="#000000" opacity={0.3} transform="translate(92 10) skewX(-8)" />
      <path d={CHAIR_BACK_D} fill="url(#hp-upholstery)" />
      <path d={CHAIR_D} fill="url(#hp-upholstery)" />
      <path d={DOILY_D} fill="#e9dcc4" opacity={0.45} />
      <path d="M 880 594 Q 960 612 1040 594 Q 1000 604 960 606 Q 920 604 880 594 Z" fill="#120a06" opacity={0.35} />
      <path d="M 884 412 Q 960 396 1036 412 L 1036 586 Q 960 596 884 586 Z" fill="#000000" opacity={0.16} />
      <path d="M 804 530 Q 830 522 860 528 M 1056 528 Q 1086 522 1116 530" stroke="#7a5038" strokeOpacity={0.5 * sun} strokeWidth={2} fill="none" />
      <path d={CHAIR_RIM_D} stroke={C.warm} strokeOpacity={0.3 * sun} strokeWidth={1.6} fill="none" />

      {/* Side table: ashtray, glass */}
      <path d={TABLE_D} fill="url(#hp-wood)" />
      <ellipse cx={TBL.x} cy={TBL.top} rx={76} ry={11} fill="#5a3a22" />
      <ellipse cx={TBL.x - 30} cy={TBL.top - 1} rx={40} ry={6} fill={C.warmHot} opacity={0.12 * sun} />
      <ellipse cx={ASH.x} cy={ASH.y} rx={25} ry={5.5} fill="#2b2622" />
      <path d={`M ${ASH.x - 25} ${ASH.y} Q ${ASH.x - 23} ${ASH.y + 9} ${ASH.x} ${ASH.y + 10} Q ${ASH.x + 23} ${ASH.y + 9} ${ASH.x + 25} ${ASH.y}`}
        fill="#4a4038" />
      <ellipse cx={ASH.x - 2} cy={ASH.y + 1} rx={15} ry={3} fill="#5e5852" />
      <path d={`M ${EMBER.x + 2} ${EMBER.y + 0.5} L ${ASH.x + 28} ${ASH.y - 10}`} stroke="#e9e1d4" strokeWidth={4.4} strokeLinecap="round" />
      <path d={`M ${ASH.x + 18} ${ASH.y - 8.6} L ${ASH.x + 28} ${ASH.y - 10}`} stroke="#c58c4a" strokeWidth={4.4} strokeLinecap="round" />
      <path d={`M ${EMBER.x - 2} ${EMBER.y + 1.5} L ${EMBER.x + 4} ${EMBER.y}`} stroke="#8d8780" strokeWidth={4.6} strokeLinecap="round" />
      {ember > 0 && (
        <>
          <circle cx={EMBER.x - 3} cy={EMBER.y + 1.5} r={9} fill={C.warm} opacity={0.25 * ember} />
          <circle cx={EMBER.x - 3} cy={EMBER.y + 1.5} r={2.6} fill="#ff7a3c" opacity={ember} />
        </>
      )}
      {smokeK > 0 && (
        <g opacity={smokeK} fill="none" strokeLinecap="round">
          <path d={smokeD(EMBER.x - 3, EMBER.y - 2, local, h0, h1)} stroke="url(#hp-smoke)" strokeWidth={6} strokeOpacity={0.18} />
          <path d={smokeD(EMBER.x - 3, EMBER.y - 2, local, h0, h1)} stroke="url(#hp-smoke)" strokeWidth={1.5} />
        </g>
      )}
      <path d={GLASS_D} fill="url(#hp-tumbler)" />
      <g clipPath="url(#hp-glass)">
        <rect x={GLASS.x - 20} y={GLASS.y - 13} width={40} height={14} fill="#a8641f" opacity={0.75} />
        <rect x={GLASS.x - 20} y={GLASS.y - 8} width={40} height={9} fill="#3a2010" opacity={0.6} />
        <rect x={GLASS.x - 20} y={GLASS.y - 52} width={40} height={52} fill="#8c8580" opacity={0.32 * dust} />
      </g>
      <path d={`M ${GLASS.x - 12} ${GLASS.y - 44} L ${GLASS.x - 11} ${GLASS.y - 8}`} stroke="#fff6e0" strokeWidth={2.2}
        strokeOpacity={0.75 - 0.5 * dust} strokeLinecap="round" />
      <ellipse cx={GLASS.x} cy={GLASS.y - 50} rx={17} ry={3} fill="none" stroke="#f3dfbd" strokeOpacity={0.55 - 0.3 * dust} strokeWidth={1.2} />

      {/* Dust in the window light */}
      {MOTES.map((m, i) => {
        const t = local * m.rate + m.ph;
        const yy = lerp(WIN.y0 + 40, 700, frac(m.v + local * 0.012 * m.rate));
        const xx = lerp(WIN.x0 + 20, WIN.x1, m.u) + (yy - WIN.y0) * 0.62 + 14 * Math.sin(t);
        const tw = 0.4 + 0.6 * Math.pow(0.5 + 0.5 * Math.sin(local * m.tw + m.ph), 2);
        return <circle key={i} cx={xx} cy={yy} r={m.size} fill="#ffe6bd" opacity={tw * 0.5 * sun} />;
      })}
    </g>
  );
}

// ===========================================================================
// 2 · The garden out back
// ===========================================================================

const GG = 712; // the row
const HZ = 560; // the far field meets the sky
const FURROWS_D = [640, 668, 700, 742, 796, 862].map((y, i) =>
  `M -440 ${y} Q 200 ${y + (i % 2 ? 6 : -5)} 800 ${y} Q 1400 ${y + (i % 2 ? -6 : 5)} 2040 ${y}`).join(" ");
// Stakes, and how far each plant's top has come loose and flopped over.
const STAKES = [
  { x: 380, top: 196, lean: -0.035, flop: 0.55, seed: 11 },
  { x: 810, top: 166, lean: 0.025, flop: 0.25, seed: 23 },
  { x: 1240, top: 214, lean: 0.05, flop: 0.9, seed: 37 },
  { x: -70, top: 236, lean: 0.03, flop: 0.6, seed: 41, small: true },
  { x: 1690, top: 200, lean: -0.03, flop: 0.4, seed: 53, small: true },
];

// A ragged leaflet: serrated ovate outline, bites out of the edge, holes.
function leaflet(r, x, y, a, len, wid) {
  const n = 6;
  const edge = (sign) => {
    const out = [];
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      let w = Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.06)), 0.85) * wid;
      if (i % 2 === 1) w *= 0.74;
      if (r() < 0.14) w *= 0.3;
      out.push([u * len, sign * w]);
    }
    return out;
  };
  const pts = [...edge(-1), ...edge(1).reverse()];
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const tr = ([px, py]) => [x + px * ca - py * sa, y + px * sa + py * ca];
  let d = pts.map((pt, i) => {
    const [qx, qy] = tr(pt);
    return `${i ? "L" : "M"} ${qx.toFixed(1)} ${qy.toFixed(1)}`;
  }).join(" ") + " Z";
  if (r() < 0.45) {
    const [hx, hy] = tr([len * (0.3 + r() * 0.4), (r() - 0.5) * wid * 0.7]);
    const hr = 1.6 + r() * wid * 0.22;
    d += ` M ${(hx - hr).toFixed(1)} ${hy.toFixed(1)} a ${hr} ${hr} 0 1 0 ${2 * hr} 0 a ${hr} ${hr} 0 1 0 ${-2 * hr} 0`;
  }
  return d;
}

const quad = (p0, p1, p2, u) => [
  (1 - u) * (1 - u) * p0[0] + 2 * (1 - u) * u * p1[0] + u * u * p2[0],
  (1 - u) * (1 - u) * p0[1] + 2 * (1 - u) * u * p1[1] + u * u * p2[1],
];

const PLANTS = STAKES.map((st) => {
  const r = rng(st.seed);
  const H = GG - st.top;
  const k = st.small ? 0.8 : 1;
  // Tied part of the stem up the stake, then the loose top arcing over.
  const stem = [];
  const tieTop = H * (0.78 - 0.18 * st.flop);
  for (let h = 0; h <= tieTop; h += 20) {
    stem.push([st.x + 10 + Math.sin(h * 0.033 + st.seed) * 5 + st.lean * h, GG - h]);
  }
  const t0 = stem[stem.length - 1];
  const side = st.lean >= 0 ? 1 : -1;
  const reach = 70 + 110 * st.flop;
  const c1 = [t0[0] + side * 26, t0[1] - 70 * (1 - st.flop * 0.6)];
  const c2 = [t0[0] + side * reach, t0[1] + reach * (0.2 + 0.5 * st.flop)];
  for (let i = 1; i <= 6; i++) stem.push(quad(t0, c1, c2, i / 6));

  const tones = [[], [], []]; // gray-green, brown, dead
  const stalks = [];
  const fruit = [];
  stem.forEach(([x, y], i) => {
    if (i < 2) return;
    const sideL = i % 2 ? 1 : -1;
    if (r() < (st.small ? 0.5 : 0.1)) return;
    const L = (66 + r() * 64) * k * (i > stem.length - 6 ? 0.8 : 1);
    const wilt = 0.25 + r() * 0.6 + (i > stem.length - 7 ? 0.3 : 0);
    const p0 = [x, y];
    const p1 = [x + sideL * L * 0.45, y - L * (0.3 - wilt * 0.2)];
    const p2 = [x + sideL * L * 0.88, y + L * (0.05 + wilt * 0.55)];
    stalks.push(`M ${p0[0].toFixed(1)} ${p0[1].toFixed(1)} Q ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`);
    const tone = r() < 0.5 ? 0 : r() < 0.65 ? 1 : 2;
    const sz = tone === 2 ? 0.7 : 1;
    [0.38, 0.62, 0.84].forEach((u) => {
      const q = quad(p0, p1, p2, u);
      const q2 = quad(p0, p1, p2, u + 0.02);
      const ang = Math.atan2(q2[1] - q[1], q2[0] - q[0]);
      [-1, 1].forEach((sgn) => {
        if (r() < 0.15) return;
        const a = ang + sgn * (0.95 - wilt * 0.2) + wilt * 0.5 * (sideL > 0 ? 1 : -1) * (sgn > 0 ? 0.4 : 0.8);
        tones[tone].push(leaflet(r, q[0], q[1], a, (24 + r() * 18) * k * sz, (11 + r() * 7) * k * sz));
      });
    });
    const angEnd = Math.atan2(p2[1] - p1[1], p2[0] - p1[0]);
    tones[tone].push(leaflet(r, p2[0], p2[1], angEnd + wilt * 0.3 * sideL, (30 + r() * 16) * k * sz, (12 + r() * 7) * k * sz));
    // A truss of fruit hanging under some of the middle leaves.
    if (!st.small && i > 4 && i < stem.length - 4 && r() < 0.22) {
      const fx = x + sideL * (14 + r() * 16);
      const fy = y + 30 + r() * 24;
      stalks.push(`M ${x} ${y + 4} Q ${fx} ${y + 6} ${fx} ${fy - 10}`);
      const n = 2 + Math.floor(r() * 2);
      for (let j = 0; j < n; j++) {
        fruit.push({ x: fx + (j - (n - 1) / 2) * 15 + (r() - 0.5) * 6, y: fy + (j % 2) * 9, r: 8 + r() * 5, kind: r() < 0.45 ? 2 : r() < 0.6 ? 1 : 0 });
      }
    }
  });
  const ties = stem.slice(0, Math.floor(tieTop / 20)).filter((_, i) => i % 3 === 2)
    .map(([x, y], i) => (i === 1 && st.flop > 0.5
      ? `M ${x - 10} ${y} q 2 10 -4 22`
      : `M ${x - 10} ${y - 2} q 6 5 15 0`))
    .join(" ");
  return {
    stake: `M ${st.x - 4} ${GG + 4} L ${st.x - 3.5 + st.lean * H} ${st.top} L ${st.x + 3.5 + st.lean * H} ${st.top - 2} L ${st.x + 4} ${GG + 4} Z`,
    stem: stem.map(([x, y], i) => `${i ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" "),
    stalks: stalks.join(" "),
    tones: tones.map((t) => t.join(" ")),
    ties,
    fruit,
    pivot: [st.x, GG - H * 0.45],
  };
});

// Tomatoes on the ground: [x, r, kind] with kind 0 dull, 1 split, 2 rotten.
const FALLEN = [
  [262, 13, 1], [318, 10, 2], [472, 14, 0], [534, 9, 2], [690, 15, 1], [905, 11, 0], [968, 14, 2],
  [1120, 12, 1], [1196, 15, 2], [1330, 11, 0], [1402, 14, 1], [80, 12, 0], [1560, 13, 2],
];
const SNACK = 690; // the rat stops at this one
// A burst tomato has slumped and split: a ragged wedge notched down into it
// from the top edge, off to one side. Drawn at r = 12 about the fruit's centre.
const SPLIT_D = "M -3.4 -6.6 L -1.7 -4.2 L -2.6 -2.8 L 0.4 1.4 L 1.2 -2.2 L 3 -3.6 L 3.8 -6.8 Z";
const SPLIT_FLESH_D = "M 1.2 -2.2 L 3 -3.6 L 3.8 -6.8 L 4.9 -6.2 L 4.1 -2.9 L 1.7 -0.9 Z";
const DROP = { x: 842, y: 470, r: 12, land: 786, rest: 748 };

const WEEDS = (() => {
  const r = rng(9393);
  return Array.from({ length: 70 }, (_, i) => {
    const near = i < 40;
    return {
      x: -260 + r() * 2120,
      h: near ? 90 + r() * 230 : 40 + r() * 120,
      lean: (r() - 0.5) * 40,
      ph: r() * 6.28,
      near,
    };
  });
})();
// Dock gone to seed between the plants: a stalk and a rusty seed spike.
const DOCKS = [[566, 380], [1022, 420], [1478, 340], [148, 300]];
const DOCK_SEEDS = (() => {
  const r = rng(4646);
  return DOCKS.map(([x, h]) => {
    const d = [];
    for (let i = 0; i < 26; i++) {
      const u = i / 26;
      const y = GG - h + u * h * 0.32;
      const w = 3 + Math.sin(u * Math.PI) * 7;
      d.push(`M ${(x + 2 + (r() - 0.5) * w * 2).toFixed(1)} ${y.toFixed(1)} h 3.4`);
    }
    return d.join(" ");
  });
})();
const CLOUDS2 = [[-200, 150, 900, 30, 3], [600, 90, 1100, 24, 2.2], [300, 260, 700, 18, 4]];
const RAT_D =
  "M -44 -4 Q -48 -30 -14 -34 Q 12 -36 28 -24 L 52 -12 Q 57 -7 50 -4 Q 34 0 20 0 L -32 0 Q -44 0 -44 -4 Z";
const CAN_D =
  "M 0 0 L 70 0 Q 78 -16 70 -34 L 0 -34 Q -8 -17 0 0 Z M 70 -26 L 126 -52 L 130 -46 L 76 -14 Z M 12 -34 Q 34 -66 58 -34 L 52 -34 Q 34 -56 18 -34 Z";

function Garden({ p, local, dur }) {
  const k = seg(p, 0.2, 0.5);
  const pan = -70 * ease.inOut(k);
  // The rat creeps along the row, stops to eat, and bolts when a tomato drops.
  const fallAt = 0.352 * dur;
  const fallT = local - fallAt;
  const tHit = Math.sqrt((2 * (GG + 4 - DROP.r - DROP.y)) / 1100);
  const landed = fallT - tHit;
  const t0 = 0.235 * dur;
  const tEat = 0.322 * dur;
  let rx;
  let pose = "creep";
  if (local < tEat) {
    const u = clamp((local - t0) / (tEat - t0));
    rx = lerp(-160, SNACK - 58, u + 0.035 * Math.sin(u * Math.PI * 7));
  } else if (landed < 0.08) {
    rx = SNACK - 58;
    pose = "eat";
  } else {
    const b = landed - 0.08;
    rx = SNACK - 58 + 700 * b * b + 420 * b;
    pose = "bolt";
  }
  const visible = local > t0 && rx < 1900;
  const gait = local * (pose === "bolt" ? 30 : 13);
  const bob = pose === "eat" ? 0 : Math.abs(Math.sin(gait)) * (pose === "bolt" ? 5 : 2.4);
  const nib = pose === "eat" ? Math.max(0, Math.sin(local * 8)) * 3.5 : 0;
  const stretch = pose === "bolt" ? 1.12 : 1;
  // The falling tomato: drops, bounces, rolls toward the rat.
  let fx = DROP.x;
  let fy = DROP.y;
  let roll = 0;
  if (fallT > 0) {
    if (landed < 0) fy = DROP.y + 550 * fallT * fallT;
    else {
      const bounce = Math.max(0, 26 * Math.sin(Math.min(Math.PI, landed * 8)) * Math.exp(-landed * 3));
      fy = GG + 4 - DROP.r - bounce;
      const u = ease.out(clamp(landed / 1.6));
      fx = lerp(DROP.x, DROP.rest, u);
      roll = (fx - DROP.x) / DROP.r;
    }
  }
  const sky = mixColor("#6b6770", "#45424c", k);
  const skyLow = mixColor("#8f877f", "#6a6260", k);

  const weedsD = (near) => WEEDS.filter((w) => w.near === near).map((w) => {
    const sw = Math.sin(local * 1.1 + w.ph) * (near ? 9 : 4) + Math.sin(local * 2.7 + w.ph * 2) * 2;
    const base = near ? 940 : GG + 6;
    return `M ${w.x.toFixed(1)} ${base} Q ${(w.x + w.lean * 0.4).toFixed(1)} ${(base - w.h * 0.6).toFixed(1)} ${(w.x + w.lean + sw).toFixed(1)} ${(base - w.h).toFixed(1)}`;
  }).join(" ");
  const ink = "#121110";
  // At the end the camera tilts up off the garden into the sky, where the
  // yard tilts down to meet it: the change rides the move, not the dissolve.
  const tilt = 620 * ease.inOut(seg(p, 0.43, 0.48));
  // ...and it opens looking down at the soil, tilting up to the row.
  const rise = 640 * (1 - ease.out(seg(p, 0.222, 0.3)));

  return (
    <g transform={`translate(0 ${tilt - rise})`}>
      <defs>
        <linearGradient id="hp-gsky" gradientUnits="userSpaceOnUse" x1="0" y1="-700" x2="0" y2={GG}>
          <stop offset="0" stopColor={mixColor(sky, "#000000", 0.55)} />
          <stop offset="0.72" stopColor={sky} />
          <stop offset="1" stopColor={skyLow} />
        </linearGradient>
        <linearGradient id="hp-ground" gradientUnits="userSpaceOnUse" x1="0" y1={HZ} x2="0" y2="920">
          <stop offset="0" stopColor="#36342d" />
          <stop offset="0.45" stopColor="#4a4238" />
          <stop offset="1" stopColor="#1e1b17" />
        </linearGradient>
        <radialGradient id="hp-tom" cx="0.36" cy="0.3">
          <stop offset="0" stopColor="#a24e36" />
          <stop offset="0.65" stopColor="#6e2a1c" />
          <stop offset="1" stopColor="#36140e" />
        </radialGradient>
        <radialGradient id="hp-tomg" cx="0.36" cy="0.3">
          <stop offset="0" stopColor="#8a8a52" />
          <stop offset="0.7" stopColor="#5c5c34" />
          <stop offset="1" stopColor="#2e2e1a" />
        </radialGradient>
        <radialGradient id="hp-rot" cx="0.4" cy="0.35">
          <stop offset="0" stopColor="#5a3a22" />
          <stop offset="1" stopColor="#24140c" />
        </radialGradient>
        <linearGradient id="hp-cloud" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8a8488" stopOpacity="0" />
          <stop offset="0.5" stopColor="#8a8488" stopOpacity="0.35" />
          <stop offset="1" stopColor="#8a8488" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect x={BLEED.x} y={BLEED.y - 700} width={BLEED.w} height={HZ - BLEED.y + 702} fill="url(#hp-gsky)" />
      {CLOUDS2.map(([x, y, w, h, v], i) => (
        <ellipse key={i} cx={x + w / 2 + v * local} cy={y} rx={w / 2} ry={h} fill="url(#hp-cloud)" />
      ))}
      <g transform={`translate(${pan * 0.3} 0)`}>
        {/* far: treeline, barn, the back of the house, the empty line */}
        <rect x={BLEED.x - 100} y={HZ} width={BLEED.w + 200} height={BLEED.y + BLEED.h - HZ} fill="url(#hp-ground)" />
        <path d={`M -440 ${HZ - 30} Q -200 ${HZ - 70} 40 ${HZ - 40} Q 300 ${HZ - 22} 520 ${HZ - 52} Q 760 ${HZ - 76} 1000 ${HZ - 46} Q 1300 ${HZ - 20} 2040 ${HZ - 40} V ${HZ + 2} H -440 Z`}
          fill={mixColor(skyLow, "#000000", 0.45)} opacity={0.8} />
        <path d={`M 60 ${HZ + 1} V ${HZ - 70} L 130 ${HZ - 116} L 200 ${HZ - 70} V ${HZ + 1} Z`} fill={mixColor(skyLow, "#000000", 0.5)} />
        <path d={`M 1500 ${GG - 40} V 300 L 1640 210 L 2060 210 V ${GG - 40} Z`} fill="#17161a" />
        <rect x={1570} y={400} width={46} height={70} fill="#0d0c0f" />
        <path d={`M 1560 ${GG - 40} V 560 h 60 V ${GG - 40}`} stroke="#0d0c0f" strokeWidth={4} fill="none" />
        <path d={`M -40 ${GG - 20} V 250 M -74 254 H -6`} stroke="#1c1b1e" strokeWidth={7} fill="none" />
        <path d={wire(-40, 258, 1500, 276, 40)} stroke="#4a464a" strokeWidth={2} fill="none" />
        {[0.18, 0.44, 0.58, 0.8].map((u) => {
          const x = lerp(-40, 1500, u);
          const y = lerp(258, 276, u) + 4 * u * (1 - u) * 80;
          return <rect key={u} x={x - 2.5} y={y - 3} width={5} height={13} fill="#3e3a36" />;
        })}
      </g>

      <g transform={`translate(${pan} 0)`}>
        <path d={FURROWS_D} stroke="#1c1915" strokeOpacity={0.5} strokeWidth={2} fill="none" />
        <path d={weedsD(false)} stroke="#33332a" strokeWidth={2} fill="none" strokeLinecap="round" />

        {PLANTS.map((pl, i) => {
          const sway = Math.sin(local * 0.8 + i * 1.3) * 0.7 + Math.sin(local * 2.1 + i) * 0.25;
          return (
            <g key={i}>
              <path d={pl.stake} fill="#2e2720" />
              <path d={pl.ties} stroke="#8a7f6c" strokeWidth={1.6} fill="none" />
              <g transform={`rotate(${sway} ${pl.pivot[0]} ${pl.pivot[1]})`}>
                <path d={pl.stem} stroke="#25251d" strokeWidth={4.5} fill="none" strokeLinejoin="round" />
                <path d={pl.stalks} stroke="#25251d" strokeWidth={2} fill="none" />
                <path d={pl.tones[2]} fill="#1e1d17" fillRule="evenodd" />
                <path d={pl.tones[1]} fill="#46372a" fillRule="evenodd" />
                <path d={pl.tones[0]} fill="#33352a" fillRule="evenodd" />
                {pl.fruit.map((f, j) => (
                  <g key={j}>
                    <circle cx={f.x} cy={f.y} r={f.r} fill={f.kind === 1 ? "url(#hp-tomg)" : f.kind === 2 ? "url(#hp-rot)" : "url(#hp-tom)"} />
                    {f.kind === 0 && <circle cx={f.x - f.r * 0.35} cy={f.y - f.r * 0.38} r={f.r * 0.2} fill="#f0c8a8" opacity={0.3} />}
                  </g>
                ))}
              </g>
            </g>
          );
        })}
        {DOCKS.map(([x, h], i) => (
          <g key={x} transform={`rotate(${Math.sin(local * 1.2 + x) * 1.6} ${x} ${GG})`}>
            <path d={`M ${x} ${GG} Q ${x + 6} ${GG - h * 0.5} ${x + 2} ${GG - h}`} stroke="#2e2a22" strokeWidth={3} fill="none" />
            <path d={DOCK_SEEDS[i]} stroke="#5a3a26" strokeWidth={3.4} strokeLinecap="round" />
          </g>
        ))}

        {/* Fruit on the ground */}
        {FALLEN.map(([x, r, kind], i) => (kind === 1 ? (
          <g key={i}>
            <ellipse cx={x} cy={GG + 6 - r * 0.6} rx={r * 1.14} ry={r * 0.64} fill="url(#hp-tom)" />
            <g transform={`translate(${x + r * (i % 2 ? 0.48 : -0.46)} ${GG + 6 - r * 0.6}) rotate(${i % 2 ? 24 : -20}) scale(${r / 12})`}>
              <path d={SPLIT_FLESH_D} fill="#b24c32" />
              <path d={SPLIT_D} fill="#1a0806" />
            </g>
          </g>
        ) : (
          <g key={i}>
            <ellipse cx={x} cy={GG + 6 - r * 0.8} rx={r * 1.06} ry={r * 0.86} fill={kind === 2 ? "url(#hp-rot)" : "url(#hp-tom)"} />
            {kind === 0 && <circle cx={x - r * 0.35} cy={GG + 6 - r * 1.2} r={r * 0.18} fill="#e8c0a0" opacity={0.3} />}
          </g>
        )))}
        {fallT > 0 && (
          <g transform={`translate(${fx} ${fy}) rotate(${(roll * 180) / Math.PI})`}>
            <circle r={DROP.r} fill="url(#hp-tom)" />
            <path d="M -3 -10 l 3 -4 l 3 4" stroke="#2c3020" strokeWidth={1.6} fill="none" />
          </g>
        )}
        <path d={`M 1160 ${GG + 2} l 150 -20 l 2 7 l -150 20 Z`} fill="#3a3029" />
        {[0, 1, 2, 3].map((i) => {
          const a = local * (3.2 + i * 0.7) + i * 2;
          return <circle key={i} cx={1196 + Math.cos(a) * (16 + i * 5)} cy={GG - 30 + Math.sin(a * 1.3) * 12 - i * 4} r={1.6} fill="#121110" />;
        })}

        {/* The rat */}
        {visible && (
          <g transform={`translate(${rx} ${GG + 22 - bob}) scale(${1.25 * stretch} ${1.25 / stretch})`} fill={ink}>
            <path d={`M -40 -6 Q -76 -4 -100 ${6 + Math.sin(local * 3) * 5} Q -118 ${12 + Math.sin(local * 3 + 1) * 6} -138 ${6 + Math.sin(local * 3 + 2) * 7}`}
              stroke={ink} strokeWidth={3.4} fill="none" strokeLinecap="round" />
            <g transform={`rotate(${pose === "eat" ? 10 + nib * 2 : 0} 20 -6)`}>
              <path d={RAT_D} />
              <circle cx={18} cy={-30} r={7} />
              <circle cx={19} cy={-30} r={3.4} fill="#3a3030" />
              <circle cx={36} cy={-18} r={1.6} fill="#6a5a50" />
              <path d="M 50 -8 l 14 -5 M 50 -7 l 15 1 M 50 -6 l 12 6" stroke="#4a4442" strokeWidth={0.8} />
            </g>
            <path d={pose === "eat"
              ? "M -26 0 l -2 8 M -10 0 l 2 8 M 16 0 l 6 8 M 28 -2 l 8 6"
              : `M -26 0 l ${Math.sin(gait) * 8} 8 M -10 0 l ${-Math.sin(gait) * 8} 8 M 16 0 l ${Math.sin(gait + 1) * 8} 8 M 28 -2 l ${-Math.sin(gait + 1) * 8} 8`}
              stroke={ink} strokeWidth={3} strokeLinecap="round" />
          </g>
        )}
      </g>

      {/* Near weeds, and an old can on its side, moving faster than the row */}
      <g transform={`translate(${pan * 1.7} 0)`}>
        <g transform="translate(150 778) rotate(-8)" fill="#0d0d0b">
          <path d={CAN_D} />
        </g>
        <path d={weedsD(true)} stroke="#11120f" strokeWidth={3.4} fill="none" strokeLinecap="round" />
      </g>
      <rect x={BLEED.x} y={BLEED.y - 700} width={BLEED.w} height={BLEED.h + 700} fill="#000000" opacity={0.06 + 0.18 * k} />
    </g>
  );
}

// ===========================================================================
// 3 · His truck
// ===========================================================================

// A stepside pickup in profile, facing right, origin on the ground at
// mid-wheelbase. One drawing serves the yard and the drive-in, so the memory
// dissolves in around it without the truck moving.
const WHEELS = [-195, 190];
const TIRE_R = 50;
const TRUCK = { x: 840, y: 736 };
const YARD_WIN = { x: 178, y: 517, w: 132 }; // the kitchen window, seen from the yard

// The yard's camera. At the end it finds the lit kitchen window and pushes
// into it; the kitchen scene arrives exactly where and as big as this leaves
// the window, so the cut is a push through the glass.
function yardCam(p) {
  const push = 1 + 0.07 * seg(p, 0.455, 0.69);
  const find = ease.inOut(seg(p, 0.7, 0.778));
  const zoom = push * (1 + 2.71 * ease.inOut(seg(p, 0.705, 0.784)));
  const fx = lerp(TRUCK.x + (800 - TRUCK.x) / push, YARD_WIN.x, find);
  const fy = lerp(560 + (450 - 560) / push, YARD_WIN.y, find);
  const ay = lerp(450, 426, find);
  return { zoom, fx, fy, ay, win: [800 + zoom * (YARD_WIN.x - fx), ay + zoom * (YARD_WIN.y - fy)] };
}
const TK = {
  bed: "M -372 -76 V -176 H -72 V -76 Z",
  rail: "M -378 -185 H -68 V -175 H -378 Z",
  rearFender: "M -286 -62 C -286 -130 -252 -154 -195 -154 C -138 -154 -104 -130 -104 -62 Z M -264 -62 A 69 69 0 0 1 -126 -62 Z",
  cab: "M -66 -64 V -238 Q -66 -272 -32 -272 L 70 -272 Q 96 -272 108 -250 L 144 -174 L 144 -64 Z",
  hood: "M 140 -176 L 296 -164 Q 334 -160 342 -130 L 346 -98 L 140 -98 Z",
  frontFender: "M 110 -60 L 130 -60 A 60 60 0 0 1 250 -60 L 314 -66 Q 342 -70 346 -98 Q 348 -128 322 -140 Q 284 -158 222 -160 Q 152 -160 128 -134 Q 112 -114 110 -88 Z",
  running: "M -108 -66 H 114 V -54 H -108 Z",
  chassis: "M -330 -78 H 320 V -56 H -330 Z",
  bumpers: "M 334 -90 H 376 Q 383 -90 383 -83 V -68 H 334 Z M -392 -94 H -366 V -76 H -392 Z",
  window: "M -50 -174 V -242 Q -50 -256 -36 -256 H 64 Q 82 -256 92 -240 L 124 -174 Z",
  seams: "M -40 -66 V -172 M 130 -66 V -172 M 142 -136 L 336 -128 M -372 -98 H -72 M -366 -176 V -80",
  stakes: "M -360 -185 v -7 h 11 v 7 Z M -236 -185 v -7 h 11 v 7 Z M -112 -185 v -7 h 11 v 7 Z",
  grille: "M 342 -126 V -94 M 349 -122 V -94",
  mirror: "M 112 -196 L 132 -208 M 128 -222 h 10 v 18 h -10 Z",
  handle: "M -30 -152 h 18 v 5 h -18 Z",
  crack: "M 20 -250 L 34 -222 L 28 -204 L 52 -178 M 34 -222 L 60 -232",
  rust:
    "M -350 -114 q 14 -16 30 -4 q 16 -8 20 10 q -8 12 -28 10 q -16 4 -22 -16 Z " +
    "M -240 -90 q 18 -10 32 2 q 8 12 -12 16 q -18 2 -20 -18 Z M -150 -160 q 14 -10 30 0 q 2 12 -14 12 q -14 0 -16 -12 Z " +
    "M 40 -92 q 12 -14 28 -2 q 10 12 -8 16 q -16 2 -20 -14 Z M 246 -126 q 16 -10 26 4 q -4 12 -18 10 q -12 -2 -8 -14 Z " +
    "M 160 -150 q 10 -6 20 2 q -2 8 -12 8 q -10 -2 -8 -10 Z",
};

function Truck({ paint, chrome, rim, rimK, glass, decay, sink, flat, grow, local, tailgate, lamp, kids, granddad, dim = 0 }) {
  const tire = (cx) => {
    const sq = flat * 14;
    const hub = mixColor(chrome, "#1a1816", decay * 0.6);
    return (
      <g key={cx}>
        <ellipse cx={cx} cy={-TIRE_R + sq * 0.5} rx={TIRE_R + sq * 0.5} ry={TIRE_R - sq * 0.5} fill="#0a0908" />
        <ellipse cx={cx} cy={-TIRE_R + sq * 0.5} rx={TIRE_R - 9 + sq * 0.4} ry={TIRE_R - 9 - sq * 0.4} fill="none" stroke="#1d1b19" strokeWidth={2} />
        <circle cx={cx} cy={-TIRE_R + sq * 0.2} r={24} fill={mixColor("#4a4440", "#221f1c", decay)} />
        <circle cx={cx} cy={-TIRE_R + sq * 0.2} r={15} fill={hub} />
        <circle cx={cx} cy={-TIRE_R + sq * 0.2} r={5} fill="#0f0d0c" />
      </g>
    );
  };
  const dark = mixColor(paint, "#000000", 0.45);
  const edge = mixColor(paint, "#000000", 0.3);
  const shine = mixColor(paint, "#ffffff", 0.14);
  return (
    <g>
      <ellipse cx={0} cy={2} rx={430} ry={22} fill="url(#hp-shadow)" />
      <g transform={`translate(0 ${sink})`}>
        <path d={TK.chassis} fill="#080706" />
        {tailgate === "down" && (
          <path d="M -372 -102 H -464 V -92 H -372 Z M -372 -176 L -460 -102 M -372 -168 L -456 -98" fill={paint} stroke={dark} strokeWidth={2} />
        )}
        {tailgate === "hanging" && (
          <path d="M -372 -84 L -384 -84 L -404 -2 L -392 0 Z M -372 -176 L -392 -6" fill={paint} stroke={dark} strokeWidth={2} />
        )}
      </g>
      {WHEELS.map(tire)}
      <g transform={`translate(0 ${sink})`}>
        <path d={TK.bed} fill={paint} />
        <path d={TK.rail} fill={mixColor(paint, "#ffffff", 0.08)} />
        <path d={TK.stakes} fill={dark} />
        <path d={TK.rearFender} fill={paint} fillRule="evenodd" stroke={edge} strokeWidth={2.4} />
        <path d="M -270 -96 C -262 -136 -236 -148 -195 -148 C -170 -148 -150 -144 -134 -134" stroke={shine} strokeWidth={2.2} fill="none" />
        <path d={TK.cab} fill={paint} />
        <path d={TK.hood} fill={paint} />
        <path d={TK.frontFender} fill={paint} stroke={edge} strokeWidth={2} />
        <path d="M 132 -128 Q 160 -154 222 -155 Q 280 -153 316 -134" stroke={shine} strokeWidth={2} fill="none" />
        <path d={TK.running} fill={dark} />
        <path d={TK.window} fill={glass} />
        {decay > 0.5 && <path d={TK.crack} stroke="#9aa4ae" strokeOpacity={0.45 * decay} strokeWidth={1} fill="none" />}
        {decay > 0 && <path d={TK.rust} fill={mixColor("#3a1c10", "#1a0e0a", dim)} opacity={0.85 * decay} />}
        <path d={TK.seams} stroke="#000000" strokeOpacity={0.4} strokeWidth={1.6} fill="none" />
        <path d={TK.handle} fill={chrome} opacity={0.8} />
        <path d={TK.mirror} stroke={dark} strokeWidth={3} fill={dark} />
        <path d={TK.bumpers} fill={chrome} />
        <path d={TK.grille} stroke={chrome} strokeWidth={2.6} />
        <circle cx={318} cy={-138} r={15} fill={chrome} />
        <circle cx={321} cy={-138} r={10} fill={decay > 0.5 ? "#0e0c0b" : lamp} />
        <rect x={-378} y={-168} width={7} height={15} fill={decay > 0.5 ? "#2a100c" : "#b0281c"} />
        {rimK > 0 && (
          <path d="M -372 -76 V -176 M -378 -185 H -68 M -66 -238 Q -66 -272 -32 -272 L 70 -272 M -286 -62 C -286 -130 -252 -154 -195 -154"
            stroke={rim} strokeOpacity={rimK} strokeWidth={1.8} fill="none" />
        )}
        {kids}
        {granddad}
      </g>
      {grow > 0 && <GrassAround local={local} grow={grow} color={mixColor("#262820", "#0b0e10", dim)} />}
    </g>
  );
}

const TUFTS = (() => {
  const r = rng(5577);
  return Array.from({ length: 140 }, (_, i) => ({
    x: -480 + r() * 960 + (i % 2 ? 0 : 0),
    h: 22 + r() * 76 * (1 - 0.4 * Math.abs(Math.sin(i))),
    lean: (r() - 0.5) * 26,
    ph: r() * 6.28,
  }));
})();
function GrassAround({ local, grow, color }) {
  const d = TUFTS.map((g) => {
    const h = g.h * grow;
    const sw = Math.sin(local * 1.3 + g.ph) * 3;
    return `M ${g.x.toFixed(1)} 6 Q ${(g.x + g.lean * 0.3).toFixed(1)} ${(-h * 0.6).toFixed(1)} ${(g.x + g.lean + sw).toFixed(1)} ${(-h).toFixed(1)}`;
  }).join(" ");
  return <path d={d} stroke={color} strokeWidth={2.6} fill="none" strokeLinecap="round" />;
}

// Kids in the bed: head and shoulders over the side rail, child-sized heads.
const BED_KIDS = [
  { x: -332, h: 34, r: 15, hair: "tail", ph: 0 },
  { x: -268, h: 46, r: 16, hair: "cow", ph: 1.4, arms: true },
  { x: -206, h: 26, r: 13.5, hair: "pig", ph: 2.2, point: true },
  { x: -146, h: 38, r: 15, hair: "short", ph: 3.1 },
];
function BedKid({ x, h, r, hair, ph, point, arms, local, fill }) {
  const top = -185 - h;
  const nod = Math.sin(local * 0.9 + ph) * 1.8;
  const hy = top + nod;
  return (
    <g fill={fill}>
      <path d={`M ${x - r * 1.6} -183 Q ${x - r * 1.6} ${hy + r * 1.75} ${x} ${hy + r * 1.45} Q ${x + r * 1.6} ${hy + r * 1.75} ${x + r * 1.6} -183 Z`} />
      <circle cx={x} cy={hy} r={r} />
      {hair === "tail" && <ellipse cx={x + r * 1.02} cy={hy + r * 0.1} rx={r * 0.3} ry={r * 0.6} transform={`rotate(-18 ${x + r} ${hy})`} />}
      {hair === "pig" && (
        <>
          <ellipse cx={x - r * 1.05} cy={hy + r * 0.25} rx={r * 0.3} ry={r * 0.55} transform={`rotate(14 ${x - r} ${hy})`} />
          <ellipse cx={x + r * 1.05} cy={hy + r * 0.25} rx={r * 0.3} ry={r * 0.55} transform={`rotate(-14 ${x + r} ${hy})`} />
        </>
      )}
      {hair === "cow" && <path d={`M ${x - 3} ${hy - r + 2} L ${x + 3} ${hy - r - 9} L ${x + 6} ${hy - r + 3} Z`} />}
      {point && (
        <path d={`M ${x - r * 0.8} ${hy + r * 1.9} L ${x - r * 3.4} ${hy - r * 0.9 + Math.sin(local * 1.7) * 3}`}
          stroke={fill} strokeWidth={r * 0.55} strokeLinecap="round" />
      )}
      {arms && (
        <path d={`M ${x - r * 1.1} ${hy + r * 1.8} L ${x - r * 1.6} ${hy - r * 0.6 + Math.sin(local * 2.3) * 4} M ${x + r * 1.1} ${hy + r * 1.8} L ${x + r * 1.7} ${hy - r * 0.5 + Math.sin(local * 2.3 + 1) * 4}`}
          stroke={fill} strokeWidth={r * 0.5} strokeLinecap="round" />
      )}
    </g>
  );
}

// The bare oak, branched once at module scope: [thick, mid, thin] paths.
const OAK = (() => {
  const r = rng(8484);
  const out = [[], [], []];
  const grow = (x, y, a, len, w, depth) => {
    const x2 = x + Math.sin(a) * len;
    const y2 = y - Math.cos(a) * len;
    const bend = (r() - 0.5) * len * 0.25;
    const cx = (x + x2) / 2 + Math.cos(a) * bend;
    const cy = (y + y2) / 2 + Math.sin(a) * bend;
    const cls = w > 9 ? 0 : w > 3.5 ? 1 : 2;
    out[cls].push(`M ${x.toFixed(1)} ${y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`);
    if (depth === 0) return;
    const n = depth > 4 ? 2 : 2 + (r() < 0.4 ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const spread = 0.35 + r() * 0.45;
      const na = a + (i - (n - 1) / 2) * spread * 1.4 + (r() - 0.5) * 0.3 + Math.sin(a) * 0.1;
      grow(x2, y2, na, len * (0.68 + r() * 0.14), w * 0.66, depth - 1);
    }
  };
  grow(1470, 740, -0.04, 200, 30, 6);
  return out.map((d) => d.join(" "));
})();
const SWING = { x: 1392, y: 470 };

const STARS2 = (() => {
  const r = rng(2727);
  return Array.from({ length: 70 }, () => ({ x: -380 + r() * 2360, y: -700 + r() * 1220, r: 0.5 + r() * 1.3, ph: r() * 6.28, rate: 0.6 + r() * 2 }));
})();
const SEDAN_D =
  "M -120 0 V -24 Q -118 -34 -100 -36 L -72 -38 Q -54 -62 -22 -64 L 28 -64 Q 54 -62 70 -40 L 104 -36 Q 120 -34 122 -22 V 0 Z";
const SCREEN = { x0: 96, x1: 660, y0: 112, y1: 396 };
const BOOTH = { x: 1470, y: 540 };
const BEAM_MOTES = (() => {
  const r = rng(8181);
  return Array.from({ length: 28 }, () => ({ u: r(), v: r(), ph: r() * 6.28, rate: 0.3 + r() * 0.8 }));
})();
const POSTS_D = Array.from({ length: 9 }, (_, i) => {
  const x = -60 + i * 190;
  return `M ${x} 700 V 668 M ${x - 7} 668 h 14 v -10 h -14 Z`;
}).join(" ");

// A bird on the cab roof; it lifts off as the memory comes in.
function Crow({ p, local, dur, ink }) {
  const tau = local - 0.522 * dur;
  const x0 = TRUCK.x + 30;
  const y0 = TRUCK.y - 272 - 5;
  if (tau < 0) {
    const peck = Math.sin(local * 1.7) > 0.8 ? 2 : 0;
    return (
      <g transform={`translate(${x0} ${y0})`} fill={ink}>
        <ellipse cx={0} cy={-7} rx={9} ry={6} />
        <circle cx={7} cy={-14 + peck} r={4.2} />
        <path d={`M 10 ${-15 + peck} l 6 2 l -6 1 Z M -7 -6 L -19 -2 L -8 -3 Z M -2 -1 v 6 M 3 -1 v 6`} stroke={ink} strokeWidth={1.2} />
      </g>
    );
  }
  if (tau > 4) return null;
  const x = x0 + 150 * tau + 20 * tau * tau;
  const y = y0 - 10 - 120 * tau + 6 * Math.sin(tau * 3);
  const f = Math.sin(tau * 15);
  const w = 15;
  return (
    <path d={`M ${x - w} ${y - 6 * f} Q ${x - w * 0.45} ${y - 2 * f - 2} ${x} ${y} Q ${x + w * 0.45} ${y - 2 * f - 2} ${x + w} ${y - 6 * f}`}
      stroke={ink} strokeWidth={3} fill="none" strokeLinecap="round" opacity={1 - seg(tau, 3, 4)} />
  );
}

function Yard({ p, local, dur }) {
  // now (dusk) -> the drive-in, years back -> now again, night, sinking
  const flash = smooth(seg(p, 0.536, 0.562)) * (1 - smooth(seg(p, 0.656, 0.682)));
  const later = smooth(seg(p, 0.656, 0.682));
  const night = smooth(seg(p, 0.6, 0.72));
  const settle = smooth(seg(p, 0.668, 0.745));
  const flat = lerp(0.28, 1, settle);
  const sink = lerp(5, 24, settle);
  const grow = lerp(0.55, 1.45, smooth(seg(p, 0.662, 0.745)));
  const tiltIn = 620 * (1 - ease.out(seg(p, 0.466, 0.53)));
  const { zoom, fx, fy, ay } = yardCam(p);
  const paintNow = mixColor(mixColor("#5d4640", "#4a403c", later), "#0f1217", night * 0.72);
  const chromeNow = mixColor("#9a948a", "#2c3036", night * 0.8);
  const glassNow = mixColor("#4a4c52", "#0b0f16", night);
  const skyTop = mixColor("#2c2d36", "#020409", night);
  const skyLow = mixColor("#8a7f7c", "#16202e", night);
  const groundNow = mixColor("#2a2924", "#0a0d12", night);
  const silNow = mixColor("#1b1a1c", "#04060a", night);
  const kitchen = 0.45 + 0.55 * night;
  const flick = 0.9 + 0.06 * Math.sin(local * 31) * Math.sin(local * 7.3) + 0.04 * Math.sin(local * 53);
  const warmRim = mixColor(C.warmHot, "#ffffff", 0.25);

  const now = (
    <g>
      <rect x={BLEED.x} y={BLEED.y - 700} width={BLEED.w} height={TRUCK.y - BLEED.y + 700} fill="url(#hp-ysky)" />
      {night > 0.05 && STARS2.slice(0, 40).map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#dfe8f4" opacity={night * (0.3 + 0.3 * Math.sin(local * s.rate + s.ph))} />
      ))}
      <path d={`M -440 ${TRUCK.y - 40} Q 200 ${TRUCK.y - 80} 700 ${TRUCK.y - 54} Q 1200 ${TRUCK.y - 30} 2040 ${TRUCK.y - 66} V ${TRUCK.y} H -440 Z`}
        fill={mixColor(skyLow, "#000000", 0.55)} />
      {/* the house: gable end, the kitchen window lit */}
      <path d={`M -220 ${TRUCK.y} V 368 L 125 186 L 470 368 V ${TRUCK.y} Z`} fill={silNow} />
      <path d="M -248 383 L 125 172 L 498 383" stroke={silNow} strokeWidth={16} fill="none" />
      <path d={`M 196 ${TRUCK.y} V 640 h 60 V ${TRUCK.y}`} fill={mixColor("#100f12", "#020306", night)} />
      {/* the kitchen window: the same proportions as the close-up it becomes */}
      <rect x={112} y={474} width={132} height={86} fill={C.warm} opacity={kitchen} />
      <rect x={112} y={474} width={132} height={86} fill="url(#hp-kwin2)" opacity={kitchen} />
      <path d="M 178 474 V 560 M 112 517 H 244" stroke={silNow} strokeWidth={4} />
      <path d="M 112 474 H 244 V 560 H 112 Z" stroke={silNow} strokeWidth={6} fill="none" />
      <ellipse cx={178} cy={TRUCK.y + 30} rx={220} ry={34} fill={C.warm} opacity={0.12 * kitchen} />
      {/* split-rail fence, a rail down */}
      <path d={`M 470 ${TRUCK.y - 76} L 1260 ${TRUCK.y - 82} M 470 ${TRUCK.y - 44} L 1000 ${TRUCK.y - 46} L 1120 ${TRUCK.y - 14} M 1140 ${TRUCK.y - 48} L 2040 ${TRUCK.y - 52}`}
        stroke={mixColor("#3a3630", "#0d1016", night)} strokeWidth={6} />
      <path d={Array.from({ length: 8 }, (_, i) => `M ${620 + i * 200} ${TRUCK.y} V ${TRUCK.y - 96}`).join(" ")}
        stroke={mixColor("#2c2924", "#0a0d12", night)} strokeWidth={9} />
      {/* the oak, and the rope where the swing was */}
      <path d={OAK[2]} stroke={silNow} strokeWidth={1.6} fill="none" strokeLinecap="round" />
      <path d={OAK[1]} stroke={silNow} strokeWidth={5} fill="none" strokeLinecap="round" />
      <path d={OAK[0]} stroke={silNow} strokeWidth={16} fill="none" strokeLinecap="round" />
      <path d={`M ${SWING.x} ${SWING.y} Q ${SWING.x + Math.sin(local * 0.9) * 6} ${SWING.y + 70} ${SWING.x + Math.sin(local * 0.9 - 0.5) * 10} ${SWING.y + 140}`}
        stroke={mixColor("#5a5248", "#1a2028", night)} strokeWidth={2.2} fill="none" />
      <rect x={BLEED.x} y={TRUCK.y} width={BLEED.w} height={BLEED.y + BLEED.h - TRUCK.y} fill={groundNow} />
      <Crow p={p} local={local} dur={dur} ink={silNow} />
      <g transform={`translate(${TRUCK.x} ${TRUCK.y})`}>
        <Truck paint={paintNow} chrome={chromeNow} rim="#d8cfc4" rimK={0.16 * (1 - night)} glass={glassNow}
          decay={lerp(0.55, 1, later)} sink={sink} flat={flat} grow={grow} local={local}
          tailgate={later > 0.5 ? "hanging" : "closed"} lamp="#d8d0c0" dim={night} />
      </g>
    </g>
  );

  const memory = flash > 0 && (
    <g opacity={flash}>
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={TRUCK.y - BLEED.y + 2} fill="url(#hp-dsky)" />
      {STARS2.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#fff1dc" opacity={0.35 + 0.35 * Math.sin(local * s.rate + s.ph)} />
      ))}
      <path d={`M -440 ${TRUCK.y - 44} Q 0 ${TRUCK.y - 86} 400 ${TRUCK.y - 58} Q 900 ${TRUCK.y - 38} 1300 ${TRUCK.y - 72} Q 1700 ${TRUCK.y - 96} 2040 ${TRUCK.y - 58} V ${TRUCK.y} H -440 Z`} fill="#0e0a0c" />
      {/* the screen on its frame, glowing and blank */}
      <ellipse cx={(SCREEN.x0 + SCREEN.x1) / 2} cy={(SCREEN.y0 + SCREEN.y1) / 2} rx={560} ry={360} fill="url(#hp-halo)" opacity={flick} />
      <path d={`M ${SCREEN.x0 + 50} ${TRUCK.y - 50} L ${SCREEN.x0 + 70} ${SCREEN.y1} M ${SCREEN.x1 - 50} ${TRUCK.y - 50} L ${SCREEN.x1 - 70} ${SCREEN.y1} ` +
        `M ${SCREEN.x0 + 60} 560 L ${SCREEN.x1 - 60} 470 M ${SCREEN.x0 + 64} 470 L ${SCREEN.x1 - 64} 560 M ${SCREEN.x0 + 56} 640 L ${SCREEN.x1 - 56} 560 M ${SCREEN.x0 + 56} 560 L ${SCREEN.x1 - 56} 640`}
        stroke="#0a0708" strokeWidth={7} />
      <rect x={SCREEN.x0 - 8} y={SCREEN.y0 - 8} width={SCREEN.x1 - SCREEN.x0 + 16} height={SCREEN.y1 - SCREEN.y0 + 16} fill="#0a0708" />
      <rect x={SCREEN.x0} y={SCREEN.y0} width={SCREEN.x1 - SCREEN.x0} height={SCREEN.y1 - SCREEN.y0} fill="url(#hp-screen)" opacity={flick} />
      {/* the beam from the booth: a soft outer cone and a brighter core */}
      <path d={`M ${BOOTH.x} ${BOOTH.y - 8} L ${SCREEN.x1} ${SCREEN.y0 - 10} L ${SCREEN.x1} ${SCREEN.y1 + 10} L ${BOOTH.x} ${BOOTH.y + 8} Z`}
        fill="url(#hp-beam2)" opacity={0.55 * flick} />
      <path d={`M ${BOOTH.x} ${BOOTH.y - 4} L ${SCREEN.x1} ${SCREEN.y0 + 30} L ${SCREEN.x1} ${SCREEN.y1 - 30} L ${BOOTH.x} ${BOOTH.y + 4} Z`}
        fill="url(#hp-beam2)" opacity={0.6 * flick} />
      {BEAM_MOTES.map((m, i) => {
        const u = frac(m.u + local * 0.012 * m.rate);
        const x = lerp(BOOTH.x, SCREEN.x1, u);
        const half = lerp(6, (SCREEN.y1 - SCREEN.y0) / 2, u);
        const y = lerp(BOOTH.y, (SCREEN.y0 + SCREEN.y1) / 2, u) + (m.v - 0.5) * 2 * half;
        return <circle key={i} cx={x} cy={y} r={1.2} fill="#fff1d6" opacity={0.6 * (0.5 + 0.5 * Math.sin(local * 3 * m.rate + m.ph))} />;
      })}
      {/* rows of cars and speaker posts, the booth */}
      <path d={POSTS_D} stroke="#120d0d" strokeWidth={3} fill="#120d0d" />
      <g fill="#0f0b0b">
        <path d={SEDAN_D} transform="translate(96 702) scale(0.62)" />
        <path d={SEDAN_D} transform="translate(1220 704) scale(0.66)" />
        <path d={SEDAN_D} transform="translate(560 694) scale(0.5)" />
      </g>
      <path d={`M ${BOOTH.x - 80} ${TRUCK.y} V ${BOOTH.y - 34} H ${BOOTH.x + 150} V ${TRUCK.y} Z`} fill="#0b0809" />
      <rect x={BOOTH.x + 40} y={BOOTH.y + 50} width={70} height={36} fill={C.warm} opacity={0.7} />
      <rect x={BOOTH.x - 9} y={BOOTH.y - 8} width={16} height={16} fill="#fff4dc" />
      <circle cx={BOOTH.x} cy={BOOTH.y} r={36} fill="url(#hp-halo)" opacity={0.8} />
      <rect x={BLEED.x} y={TRUCK.y} width={BLEED.w} height={BLEED.y + BLEED.h - TRUCK.y} fill="#0d0a09" />
      <path d={`M -440 ${TRUCK.y + 1} H 2040`} stroke="#2a1e18" strokeWidth={2} />
      <g transform={`translate(${TRUCK.x} ${TRUCK.y})`}>
        <Truck paint="#1c1312" chrome="#7a6a5a" rim={warmRim} rimK={0.5 * flick} glass="#2c1f1a" decay={0} sink={0} flat={0} grow={0}
          local={local} tailgate="down" lamp="#3a2a20"
          kids={BED_KIDS.map((kd, i) => <BedKid key={i} {...kd} local={local} fill="#130c0b" />)}
          granddad={(
            <>
              <Worker x={-420} y={-102 + 45 * 1.3} s={1.3} color="#100a09" facing={-1} pose="sit" hat="cap" />
              <circle cx={-420 - 1.3 * 46} cy={-102 + 45 * 1.3 - 1.3 * 97} r={2.6} fill="#ff8a4a" opacity={0.65 + 0.35 * Math.sin(local * 2.2)} />
            </>
          )}
        />
      </g>
    </g>
  );

  return (
    <g transform={`translate(800 ${ay + tiltIn}) scale(${zoom}) translate(${-fx} ${-fy})`}>
      <defs>
        <radialGradient id="hp-shadow">
          <stop offset="0" stopColor="#000000" stopOpacity="0.5" />
          <stop offset="0.7" stopColor="#000000" stopOpacity="0.25" />
          <stop offset="1" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="hp-ysky" gradientUnits="userSpaceOnUse" x1="0" y1="-600" x2="0" y2={TRUCK.y}>
          <stop offset="0" stopColor={skyTop} />
          <stop offset="1" stopColor={skyLow} />
        </linearGradient>
        <linearGradient id="hp-dsky" gradientUnits="userSpaceOnUse" x1="0" y1="-600" x2="0" y2={TRUCK.y}>
          <stop offset="0" stopColor="#07060d" />
          <stop offset="0.7" stopColor="#1c1322" />
          <stop offset="1" stopColor="#3c2430" />
        </linearGradient>
        <radialGradient id="hp-halo">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.5" />
          <stop offset="0.5" stopColor={C.warm} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hp-screen" cx="0.5" cy="0.45">
          <stop offset="0" stopColor="#fffaf0" />
          <stop offset="0.7" stopColor="#f7e2bc" />
          <stop offset="1" stopColor="#d9b07c" />
        </radialGradient>
        <radialGradient id="hp-kwin2" cx="0.5" cy="0.3">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.9" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0.4" />
        </radialGradient>
        <linearGradient id="hp-beam2" gradientUnits="userSpaceOnUse" x1={BOOTH.x} y1="0" x2={SCREEN.x1} y2="0">
          <stop offset="0" stopColor="#fff4dc" stopOpacity="0.55" />
          <stop offset="1" stopColor="#ffe2b0" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      {now}
      {memory}
      {flash > 0 && flash < 1 && (
        <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill={C.warmHot} opacity={0.1 * Math.sin(Math.PI * flash)} />
      )}
    </g>
  );
}

// ===========================================================================
// 4 · The kitchen window at night
// ===========================================================================

const KW = { x0: 492, x1: 1108, y0: 226, y1: 626, mx: 800, my: 426 };
const CLAP_D = Array.from({ length: 64 }, (_, i) => `M -440 ${-200 + i * 19} H 2040`).join(" ");
const SNOW = (() => {
  const r = rng(6464);
  return Array.from({ length: 48 }, () => ({ x: r(), y: r(), v: 0.5 + r() * 0.7, size: 0.8 + r() * 1.7, ph: r() * 6.28 }));
})();
const ICICLES_D = Array.from({ length: 40 }, (_, i) => {
  const x = -420 + i * 62 + ((i * 37) % 23);
  const h = 10 + ((i * 53) % 29);
  return `M ${x} 106 l 4 ${h} l 4 ${-h} Z`;
}).join(" ");
const KT = { x0: 604, x1: 1130, top: 548 };
// Six kids round the table: four on the far side, two with their backs to us.
const KIDS6 = [
  { x: 684, top: 454, r: 16, hair: "tail", ph: 0.3 },
  { x: 746, top: 480, r: 13, hair: "short", ph: 1.7 },
  { x: 866, top: 446, r: 17, hair: "cow", ph: 2.6 },
  { x: 958, top: 466, r: 15, hair: "bob", ph: 4.1 },
  { x: 724, top: 516, r: 19, hair: "short", ph: 0.9, near: true },
  { x: 1006, top: 522, r: 18, hair: "pig", ph: 3.3, near: true },
];
// The head of the table: a ladder-back chair, nobody in it.
const CHAIR_HEAD_D =
  "M 540 652 V 392 h 9 V 652 Z M 602 652 V 392 h 9 V 652 Z " +
  "M 549 412 h 53 v 10 h -53 Z M 549 440 h 53 v 10 h -53 Z M 549 468 h 53 v 10 h -53 Z " +
  "M 528 566 H 622 L 628 580 H 522 Z M 528 580 V 652 h 8 V 580 Z M 614 580 V 652 h 8 V 580 Z";
const VALANCE_D = (() => {
  let d = `M ${KW.x0} ${KW.y0} H ${KW.x1} V ${KW.y0 + 30}`;
  for (let x = KW.x1; x > KW.x0; x -= 40) d += ` Q ${x - 20} ${KW.y0 + 46} ${x - 40} ${KW.y0 + 30}`;
  return d + " Z";
})();

function Child({ x, top, r, hair, near, local, ph, fill }) {
  const nod = Math.sin(local * 0.8 + ph) * (near ? 1.4 : 2);
  const hy = top + nod;
  const base = near ? 700 : KT.top + 2;
  const sw = r * (near ? 2.05 : 1.75);
  const swing = Math.sin(local * 1.1 + ph) * 3;
  return (
    <g fill={fill}>
      <path d={`M ${x - sw} ${base} Q ${x - sw} ${hy + r * 1.75} ${x} ${hy + r * 1.42} Q ${x + sw} ${hy + r * 1.75} ${x + sw} ${base} Z`} />
      <circle cx={x} cy={hy} r={r} />
      {hair === "tail" && <ellipse cx={x - r * 1.05 + swing * 0.3} cy={hy + r * 0.15} rx={r * 0.32} ry={r * 0.62} transform={`rotate(${18 + swing} ${x - r} ${hy})`} />}
      {hair === "pig" && (
        <>
          <ellipse cx={x - r * 1.08} cy={hy + r * 0.25} rx={r * 0.3} ry={r * 0.55} transform={`rotate(${12 + swing} ${x - r} ${hy})`} />
          <ellipse cx={x + r * 1.08} cy={hy + r * 0.25} rx={r * 0.3} ry={r * 0.55} transform={`rotate(${-12 + swing} ${x + r} ${hy})`} />
        </>
      )}
      {hair === "cow" && <path d={`M ${x - 4} ${hy - r + 2} L ${x + 2} ${hy - r - 9} L ${x + 5} ${hy - r + 3} Z`} />}
      {hair === "bob" && <path d={`M ${x - r * 1.14} ${hy + r * 0.55} Q ${x - r * 1.2} ${hy - r * 1.12} ${x} ${hy - r * 1.08} Q ${x + r * 1.2} ${hy - r * 1.12} ${x + r * 1.14} ${hy + r * 0.55} Z`} />}
    </g>
  );
}

function Kitchen({ p, local }) {
  // It arrives where the yard's push has the window, at the same width, then
  // carries on in to full frame.
  const cam = yardCam(p);
  const matched = (z) => (z * YARD_WIN.w) / (KW.x1 - KW.x0);
  const arrive = p < 0.784 ? matched(cam.zoom) : lerp(matched(yardCam(0.784).zoom), 1, ease.inOut(seg(p, 0.784, 0.86)));
  const [ax, ayy] = cam.win;
  const push = 1 + 0.12 * ease.inOut(seg(p, 0.79, 1));
  const cx = 800;
  const cy = 450;
  const bulbX = 836 + Math.sin(local * 0.9) * 2.2;
  const wood = "#2a160b";
  const kid = "#26130a";

  return (
    <g>
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="#0b1118" />
      <g transform={`translate(${ax} ${ayy}) scale(${arrive}) translate(${-(KW.x0 + KW.x1) / 2} ${-(KW.y0 + KW.y1) / 2}) translate(${cx} ${cy}) scale(${push}) translate(${-cx} ${-cy})`}>
        <defs>
          <radialGradient id="hp-kglow" gradientUnits="userSpaceOnUse" cx={bulbX} cy={330} r={460}>
            <stop offset="0" stopColor="#ffe3b4" />
            <stop offset="0.4" stopColor="#dd9d5c" />
            <stop offset="1" stopColor="#6a391b" />
          </radialGradient>
          <linearGradient id="hp-cloth" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f1dcb2" />
            <stop offset="1" stopColor="#b8875a" />
          </linearGradient>
          <radialGradient id="hp-spill" gradientUnits="userSpaceOnUse" cx={800} cy={760} r={520}>
            <stop offset="0" stopColor={C.warm} stopOpacity="0.38" />
            <stop offset="0.5" stopColor={C.warm} stopOpacity="0.1" />
            <stop offset="1" stopColor={C.warm} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="hp-frost" gradientUnits="userSpaceOnUse" x1="0" y1={KW.y1} x2="0" y2={KW.y1 - 90}>
            <stop offset="0" stopColor="#e6eef6" stopOpacity="0.3" />
            <stop offset="1" stopColor="#e6eef6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="hp-night" gradientUnits="userSpaceOnUse" x1="0" y1="-900" x2="0" y2="900">
            <stop offset="0" stopColor="#020407" />
            <stop offset="1" stopColor="#0a1018" />
          </linearGradient>
          <linearGradient id="hp-wallshade" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="1600" y2="0">
            <stop offset="0" stopColor="#000000" stopOpacity="0.55" />
            <stop offset="0.5" stopColor="#000000" stopOpacity="0" />
            <stop offset="1" stopColor="#000000" stopOpacity="0.55" />
          </linearGradient>
          <pattern id="hp-gingham" width="12" height="12" patternUnits="userSpaceOnUse">
            <rect width="12" height="12" fill="#e8c9a0" />
            <rect width="6" height="12" fill="#a8452c" opacity="0.45" />
            <rect width="12" height="6" fill="#a8452c" opacity="0.45" />
          </pattern>
          <clipPath id="hp-kwin">
            <rect x={KW.x0} y={KW.y0} width={KW.x1 - KW.x0} height={KW.y1 - KW.y0} />
          </clipPath>
        </defs>

        <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#hp-night)" />
        {/* clapboard wall, eave and icicles */}
        <rect x={BLEED.x} y={-200} width={BLEED.w} height={960} fill="#0d131b" />
        <path d={CLAP_D} stroke="#05080c" strokeWidth={3} />
        <path d={CLAP_D} stroke="#1a2531" strokeWidth={1} transform="translate(0 2.5)" />
        <path d="M -440 -900 H 2040 V 106 H -440 Z" fill="#04060a" />
        <path d={ICICLES_D} fill="#c9d6e2" opacity={0.4} />
        <rect x={BLEED.x} y={-200} width={BLEED.w} height={960} fill="url(#hp-wallshade)" />

        {/* the kitchen */}
        <g clipPath="url(#hp-kwin)">
          <rect x={KW.x0} y={KW.y0} width={KW.x1 - KW.x0} height={KW.y1 - KW.y0} fill="url(#hp-kglow)" />
          <path d={`M ${KW.x0} 300 H 640 V 430 H ${KW.x0} Z`} fill="#6a3d1f" opacity={0.45} />
          <rect x={1030} y={290} width={78} height={6} fill="#3a1e0e" />
          <path d="M 1040 290 v -18 h 12 v 18 Z M 1060 290 v -26 h 14 v 26 Z M 1082 290 v -14 h 18 v 14 Z" fill="#3a1e0e" />
          <rect x={538} y={318} width={60} height={70} fill="#e6c79c" opacity={0.55} />
          <path d="M 538 336 H 598 M 538 352 H 598 M 538 368 H 598 M 558 336 V 388 M 578 336 V 388" stroke="#6a3d1f" strokeWidth={1} opacity={0.6} />
          {/* the empty chair at the head */}
          <ellipse cx={575} cy={600} rx={90} ry={60} fill="#ffe7c0" opacity={0.18} />
          <path d={CHAIR_HEAD_D} fill={wood} />
          <path d="M 540 392 h 9 M 602 392 h 9 M 528 566 H 622" stroke="#f3c48a" strokeOpacity={0.5} strokeWidth={1.4} />
          {/* far side of the table */}
          {KIDS6.filter((kd) => !kd.near).map((kd, i) => <Child key={i} {...kd} local={local} fill={kid} />)}
          <path d="M 878 540 h 44 v -22 q -22 -9 -44 0 Z M 1070 540 v -30 q 8 -6 16 0 v 30 Z M 770 540 q 18 -14 36 0 Z" fill="#3a1d0d" />
          {[0, 1, 2].map((i) => {
            const t = frac(local * 0.22 + i / 3);
            return (
              <path key={i} d={`M ${894 + i * 6} ${514 - t * 80} q ${7 * Math.sin(local + i)} -11 0 -22`} stroke="#fff3dc"
                strokeOpacity={0.4 * Math.sin(Math.PI * t)} strokeWidth={3} fill="none" strokeLinecap="round" />
            );
          })}
          <rect x={KT.x0} y={KT.top} width={KT.x1 - KT.x0} height={44} fill="url(#hp-cloth)" />
          <path d={`M ${KT.x0} ${KT.top + 44} H ${KT.x1}`} stroke="#7a4a28" strokeWidth={2} />
          <path d={`M ${KT.x0} ${KT.top + 1} H ${KT.x1}`} stroke="#fff3dc" strokeOpacity={0.6} strokeWidth={1.5} />
          <path d={`M ${KT.x0 + 14} ${KT.top + 44} V 640 M ${KT.x1 - 14} ${KT.top + 44} V 640`} stroke={wood} strokeWidth={10} />
          <ellipse cx={684} cy={KT.top + 3} rx={20} ry={3} fill="#fff6e4" opacity={0.6} />
          <ellipse cx={866} cy={KT.top + 3} rx={22} ry={3} fill="#fff6e4" opacity={0.6} />
          <ellipse cx={958} cy={KT.top + 3} rx={20} ry={3} fill="#fff6e4" opacity={0.6} />
          {/* near side, backs to us */}
          {KIDS6.filter((kd) => kd.near).map((kd, i) => <Child key={i} {...kd} local={local} fill="#170b05" />)}
          {/* bulb and its valance-shaded top */}
          <path d={`M 836 ${KW.y0} L ${bulbX} 316`} stroke="#3a1e0e" strokeWidth={2} />
          <circle cx={bulbX} cy={326} r={50} fill="#fff1d0" opacity={0.22} />
          <circle cx={bulbX} cy={326} r={10} fill="#fff7e6" />
          <path d={VALANCE_D} fill="url(#hp-gingham)" opacity={0.92} />
          <path d={VALANCE_D} fill="#000000" opacity={0.18} />
        </g>
        {/* frost along the sill, sashes, trim */}
        <rect x={KW.x0} y={KW.y1 - 90} width={KW.x1 - KW.x0} height={90} fill="url(#hp-frost)" />
        <path d={`M ${KW.x0} ${KW.y0} H ${KW.x1} V ${KW.y1} H ${KW.x0} Z M ${KW.mx} ${KW.y0} V ${KW.y1}`}
          stroke="#0e141b" strokeWidth={9} fill="none" />
        <path d={`M ${KW.x0} ${KW.my} H ${KW.x1}`} stroke="#0e141b" strokeWidth={16} />
        <path d={`M ${KW.x0 - 30} ${KW.y0 - 30} H ${KW.x1 + 30} V ${KW.y1 + 4} H ${KW.x0 - 30} Z M ${KW.x0 - 6} ${KW.y0 - 6} H ${KW.x1 + 6} V ${KW.y1} H ${KW.x0 - 6} Z`}
          fill="#17212c" fillRule="evenodd" />
        <path d={`M ${KW.x0 - 30} ${KW.y0 - 30} H ${KW.x1 + 30}`} stroke="#2a3a4c" strokeWidth={2} />
        <rect x={KW.x0 - 46} y={KW.y1 + 2} width={KW.x1 - KW.x0 + 92} height={18} fill="#1b2633" />
        <path d={`M ${KW.x0 - 46} ${KW.y1 + 3} Q ${KW.x0 + 60} ${KW.y1 - 10} ${KW.x0 + 220} ${KW.y1 - 3} Q 800 ${KW.y1 - 14} ${KW.x1 - 120} ${KW.y1 - 4} Q ${KW.x1 + 30} ${KW.y1 - 11} ${KW.x1 + 46} ${KW.y1 + 3} Z`}
          fill="#dfe8f0" opacity={0.88} />

        {/* outside: snow on the ground, the window's light on it */}
        <rect x={BLEED.x} y={760} width={BLEED.w} height={BLEED.y + BLEED.h - 760} fill="#141e2a" />
        <path d="M -440 764 Q 200 744 800 758 Q 1300 768 2040 748" stroke="#2c3d50" strokeWidth={3} fill="none" />
        <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#hp-spill)" />
        <path d="M 300 764 Q 330 700 392 716 Q 440 672 494 716 Q 536 704 548 764 Z M 1052 764 Q 1072 706 1126 718 Q 1170 676 1222 716 Q 1276 702 1306 764 Z"
          fill="#05080c" />
        <path d="M -440 136 Q -200 176 60 146 Q 200 126 300 76 M 60 146 Q 120 186 200 184 M -100 162 Q -40 116 20 56"
          stroke="#03050a" strokeWidth={14} fill="none" strokeLinecap="round" />
        {SNOW.map((sn, i) => {
          const y = -120 + frac(sn.y + local * 0.035 * sn.v) * 1040;
          const x = -200 + sn.x * 2000 + 20 * Math.sin(local * 0.6 + sn.ph);
          return <circle key={i} cx={x} cy={y} r={sn.size} fill="#e6eef6" opacity={0.6} />;
        })}
      </g>
    </g>
  );
}

// ===========================================================================

export default function Homeplace({ p, local, dur }) {
  const k2 = smooth(seg(p, 0.218, 0.24));
  const k3 = smooth(seg(p, 0.466, 0.488));
  const k4 = smooth(seg(p, 0.766, 0.784));
  return (
    <g>
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="#000000" />
      {k2 < 1 && <Parlor p={p} local={local} dur={dur} />}
      {k2 > 0 && k3 < 1 && (
        <g opacity={k2}>
          <Garden p={p} local={local} dur={dur} />
        </g>
      )}
      {k3 > 0 && k4 < 1 && (
        <g opacity={k3}>
          <Yard p={p} local={local} dur={dur} />
        </g>
      )}
      {k4 > 0 && (
        <g opacity={k4}>
          <Kitchen p={p} local={local} dur={dur} />
        </g>
      )}
    </g>
  );
}
